import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchPOCsByRegion as apiFetchPOCsByRegion, searchPOCs as apiSearchPOCs } from '../../services/api';

export const normalizeRegionValue = (region) => {
  if (!region) return null;
  if (typeof region === 'string') return region.trim() || null;
  if (typeof region === 'object') {
    return (region.value || region.label || '').trim() || null;
  }
  return String(region).trim() || null;
};

const usePOCs = (selectedRegion = null) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pocs, setPocs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, returned: 0 });
  const regionRef = useRef(normalizeRegionValue(selectedRegion));

  useEffect(() => {
    regionRef.current = normalizeRegionValue(selectedRegion);
  }, [selectedRegion]);

  const resetState = useCallback((limit) => {
    setPocs([]);
    setTotalCount(0);
    setPagination({ skip: 0, limit, returned: 0 });
  }, []);

  const applyResult = useCallback((result, { skip, limit, append }) => {
    const data = result?.data || {};
    const newPocs = data.pocs || [];
    const newTotal = data.total_count || 0;
    const newPagination = data.pagination || { skip, limit, returned: newPocs.length || 0 };

    if (append) {
      setPocs((prev) => [...prev, ...newPocs]);
    } else {
      setPocs(newPocs);
    }

    setTotalCount(newTotal);
    setPagination(newPagination);

    return data;
  }, []);

  const fetchPOCsByRegion = useCallback(async (region, skip = 0, limit = 1000, append = false) => {
    const normalizedRegion = normalizeRegionValue(region);
    if (!normalizedRegion) {
      setError('Region is required');
      return;
    }

    regionRef.current = normalizedRegion;
    setLoading(true);
    setError(null);

    try {
      // console.log(`Fetching POCs: region=${normalizedRegion}, skip=${skip}, limit=${limit}, append=${append}`);
      const result = await apiFetchPOCsByRegion(normalizedRegion, skip, limit);
      return applyResult(result, { skip, limit, append });
    } catch (err) {
      console.error('Error fetching POCs by region:', err);
      setError(err.message || 'Failed to fetch POCs');
      if (!append) {
        resetState(limit);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyResult, resetState]);

  const searchPOCs = useCallback(async (query, skip = 0, limit = 100, append = false) => {
    if (!query || query.trim().length < 2) {
      setError('Search query must be at least 2 characters');
      return;
    }

    const activeRegion = regionRef.current;
    if (!activeRegion) {
      setError('Region is required for POC search');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // console.log(
      //   `Searching POCs: query=${query}, region=${activeRegion}, skip=${skip}, limit=${limit}, append=${append}`
      // );
      const result = await apiSearchPOCs(query, activeRegion, skip, limit);
      return applyResult(result, { skip, limit, append });
    } catch (err) {
      console.error('Error searching POCs:', err);
      setError(err.message || 'Failed to search POCs');
      if (!append) {
        resetState(limit);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyResult, resetState]);

  const clearPOCs = useCallback(() => {
    resetState(50);
    setError(null);
  }, [resetState]);

  const loadMorePOCs = useCallback(async (region, query) => {
    const currentSkip = pagination.skip + pagination.returned;
    const activeRegion = normalizeRegionValue(region) || regionRef.current;

    // console.log('loadMorePOCs called:', { region: activeRegion, query, currentSkip, totalCount, pagination });

    if (currentSkip >= totalCount) {
      // console.log('No more data to load');
      return;
    }

    if (query && query.trim().length >= 2) {
      // console.log('Loading more with search query');
      return await searchPOCs(query, currentSkip, pagination.limit, true);
    }

    if (activeRegion) {
      // console.log('Loading more with region');
      return await fetchPOCsByRegion(activeRegion, currentSkip, pagination.limit, true);
    }
  }, [pagination, totalCount, searchPOCs, fetchPOCsByRegion]);

  return {
    loading,
    error,
    pocs,
    totalCount,
    pagination,
    fetchPOCsByRegion,
    searchPOCs,
    loadMorePOCs,
    clearPOCs,
    hasMorePOCs: pagination.skip + pagination.returned < totalCount,
    isSearching: !!error || loading,
  };
};

export default usePOCs;
